/*
 * LagerApp.tsx
 *
 * Based in lg_views.py
 * Created by Christian Klein on 2011-12-20.
 * Copyright (c) 2011, 2012, 2014, 2016, 2017, 2019 HUDORA. All rights reserved.
 *
 * Created by Dr. Maximillian Dornseif 2022-04-04 in huWaWi3 27.0.0
 * Copyright (c) 2011, 2012, 2014, 2016, 2017, 2019, 2022 HUDORA GmbH
 */

import { ISidebarProps, WithSidebar } from 'fluentui-hooks';
import gql from 'graphql-tag';
import React from 'react';
import { WaWiConfigTemplatesApp } from 'WaWi/WaWiApp';

import { RESERVIERUNGSPOSITION_FRAGMENT } from '../fragments';
import { KindPage } from '../lib/components/KindPage';
import { IcBestandsbuchApp, IcBestandsbuchUnverbuchtApp } from './IcBestandsbuchApp';
import { IcConfigApp } from './IcConfigApp';
import { IcFmtpApp } from './IcFmtpApp';
import { getIinventurSidebarLinks } from './IcInventurApp';
import { IcKommiApp } from './IcKommiApp';
import { IcLagerApp } from './IcLagerApp';
import { IcSkuMastersApp } from './IcSkuMastersApp';
import { IcSkusApp } from './IcSkusApp';
import { IcWertkorrekturenApp } from './IcWertkorrekturApp';
import { IcZuteilungenApp } from './IcZuteilungenApp';

export const IcApp = (props) => {
  const sidebar: ISidebarProps = {
    Bewegungen: {
      collapseByDefault: false,
      links: [
        {
          title: 'Kommi Aufträge',
          path: '/ic/kommi',
          component: (
            <div className="authEnforced">
              <IcKommiApp />
            </div>
          ),
        },
        { title: 'Zuteilungen', path: '/ic/zuteilung', component: <IcZuteilungenApp /> },
        // {
        //   title: 'Bestellungen',
        //   path: '/ic/po',
        //   component: <            IcBestellungenVereinnahmen />
        //   ,
        // },
      ],
    },
    Buchungen: {
      collapseByDefault: false,
      links: [
        {
          title: 'Bestandsbuch',
          path: '/ic/bestandsbuch',
          component: (
            <div className="authEnforced">
              <IcBestandsbuchApp />
            </div>
          ),
        },
        {
          title: '- unverbucht',
          path: '/ic/unverbuchtesBestandsbuch',
          component: (
            <div className="authEnforced">
              <IcBestandsbuchUnverbuchtApp />
            </div>
          ),
        },
        {
          title: 'Wertkorrekturen',
          path: '/ic/wertkorrektur',
          component: (
            <div className="authEnforced">
              <IcWertkorrekturenApp />
            </div>
          ),
        },
        {
          title: 'Neuverbuchung',
          path: '/ic/neuverbuchung',
          component: (
            <KindPage
              basePath={'/ic'}
              componentPath={'neuverbuchung'}
              schemaName={'HwIcNeuverbuchungswunsch'}
              title={'Neuverbuchungs-Wünsche'}
              listQuery={NEUVERBUCHUNGS_LIST_QUERY}
              entityQuery={NEUVERBUCHUNGS_ENTITY_QUERY}
            ></KindPage>
          ),
        },
        {
          title: 'SKU-Sperren',
          path: '/ic/icSperren',
          component: (
            <div className="authEnforced">
              <KindPage
                basePath={'/ic'}
                componentPath={'icSperren'}
                schemaName={'HwIcArtikelConfig'}
                title={'SKU-Sperren'}
                listQuery={SPERREN_LIST_QUERY}
                entityQuery={SPERREN_ENTITY_QUERY}
              ></KindPage>
            </div>
          ),
        },
      ],
    },
    Stammdaten: {
      collapseByDefault: false,
      links: [
        {
          title: 'Läger',
          path: '/ic/lager',
          component: (
            <div className="authEnforced">
              <IcLagerApp />
            </div>
          ),
        },
        { title: 'SKU Master', path: '/ic/mastersku', component: <IcSkuMastersApp /> },
        { title: 'SKUs', path: '/ic/sku', component: <IcSkusApp /> },
      ],
    },
    Jahresinventur: {
      collapseByDefault: false,
      links: getIinventurSidebarLinks(),
    },
    System: {
      collapseByDefault: false,
      links: [
        {
          title: 'Konfiguration',
          path: '/ic/config/SYSCONF1',
          component: <IcConfigApp />,
        },
        { title: 'FMTP', path: '/ic/fmtp', component: <IcFmtpApp /> },
      ],
    },
  };

  return (
    <React.StrictMode>
      <WithSidebar items={sidebar} />
    </React.StrictMode>
  );
};

export const NEUVERBUCHUNGS_LIST_QUERY = gql`
  query Neuverbuchungswunsch($cursor: String) {
    nodes: hwIcNeuverbuchungswunschPages(after: $cursor) {
      edges {
        cursor
        node {
          artnr
          created_at
          designator
          grund
          id
          timestamp
          updated_at
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
export const NEUVERBUCHUNGS_ENTITY_QUERY = gql`
  query EinNeuverbuchungswunsch($nr: ID!) {
    entity: hwIcNeuverbuchungswunsch(designator: $nr) {
      artnr
      created_at
      designator
      grund
      id
      timestamp
      updated_at
    }
  }
`;
export const SPERREN_LIST_QUERY = gql`
  query Sperren($cursor: String) {
    nodes: hwIcArtikelConfigPages(after: $cursor) {
      edges {
        node {
          wert
          updated_at
          name
          grund
          designator
          created_at
          artnr
          _keyStr
          id
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
export const SPERREN_ENTITY_QUERY = gql`
  query Sperre($nr: ID!) {
    entity: hwIcArtikelConfig(designator: $nr) {
      wert
      updated_at
      name
      grund
      designator
      created_at
      artnr
      _keyStr
      id
    }
  }
`;
export const RESERVIERUNGSPOS_LIST_QUERY = gql`
  query ReservierungsPositionen($cursor: String) {
    nodes: hwReservierungsPositionPages(after: $cursor, filter: { erledigt: { eq: false } }) {
      edges {
        cursor
        node {
          ...resevierungspositionFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${RESERVIERUNGSPOSITION_FRAGMENT}
`;

export const LAGERLISTE_QUERY = gql`
  query ItemPreisListe($cursor: String) {
    nodes: h3ItemPages(after: $cursor, filter: { ist_aktiv: { eq: true } }) {
      edges {
        cursor
        node {
          id
          designator
          name
          forDesignator
          ean
          zukunft {
            buchbestand
            frei_ab
            frei_max
            freie_menge
            naechster_zulauf
            zukuenftiger_zulauf
          }
          preisResolved {
            bartnr
            fh
            uvp
          }
          skuSum {
            mngEx
            mngPal
          }
          produkt {
            kategorie
          }
          pim {
            einstufung
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
